<template>
  <v-row no-gutters>
    <NumberOfShipmentsEditionDialog
      v-model="numberOfShipmentsEditionDialog"
      :info="shippingModalInfo"
      @changeShipping="changeShipping"
    />

    <v-col cols="12" lg="8">
      <p class="text-h2 mb-12">
        {{ canEditOffer ? $t('STEPPER_STEPINIT_TITLE')
                        : $t('STEPPER_STEPINIT_LABEL') }}
      </p>

      <v-form ref="form" lazy-validation>
        <v-text-field
          v-if="isStep1ReadOnly || !canEditOffer"
          readonly
          outlined
          class="select-field--reduced mt-12 text-body-2"
          :value="tariffSelectedLabel"
          @focus="$event.target.blur()"
        />

        <v-select
          v-else
          :items="rateModelsAvailable"
          item-text="description"
          item-value="id"
          class="select-field--reduced mt-12 text-body-2"
          :label="$t('STEPPER_STEPINIT_FIELD')"
          outlined
          v-model="tariffSelected"
          @change="selectTariff"
          :rules="[tariffRules.required]"
          data-cy="select-tariff-model"
        />

        <v-row v-if="shouldShowDistributionFields" class="mt-4">
          <v-col cols="6">
            <v-text-field
              v-model.number="b2b"
              type="number"
              min="0"
              max="100"
              outlined
              class="text-body-2"
              :label="$t('STEPPER_DISTRIBUTION_B2B')"
              @input="() => validateDistribution('b2b')"
              @keypress="numberOnly"
              :rules="[numericRules.numberOnly]"
              data-cy="distribution-b2b"
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model.number="b2c"
              type="number"
              min="0"
              max="100"
              outlined
              class="text-body-2"
              :label="$t('STEPPER_DISTRIBUTION_B2C')"
              @input="() => validateDistribution('b2c')"
              @keypress="numberOnly"
              :rules="[numericRules.numberOnly]"
              data-cy="distribution-b2c"
            />
          </v-col>  
        </v-row>

        <v-select
          v-if="(tariffSelected !== 0 || isStep1ReadOnly) && legalCities.length"
          :disabled="isOfferSignedOrSigning"
          :items="legalCities"
          item-text="description"
          item-value="description"
          class="select-field--reduced mt-12 text-body-2"
          :label="$t('STEPPER_LEGAL_CITY_FIELD')"
          outlined
          v-model="legalCity"
          @change="selectLegalCity"
          :rules="[tariffRules.required]"
          data-cy="select-legal-city"
        />

        <div v-if="showServicePoints">
          <v-radio-group v-model="favoriteServicePoint" required>
            <v-simple-table class="mt-4" data-cy="service-points-table">
              <template v-slot:default>
                <caption class="body-1 body-1--bold text-left">
                  {{ $t('STEPPER_STEPINIT_ADDRESSES_LEAD') }}
                </caption>

                <col width="60px" />
                <col width="auto" />
                <col width="min(130px, 90px)" />

                <thead>
                  <tr>
                    <th class="text-left">{{ $t('STEPPER_STEPINIT_MAIN_ADDRESS') }}</th>
                    <th class="text-left">{{ $t('STEPPER_STEPINIT_ADDRESS') }}</th>
                    <th class="text-left">{{ $t('STEPPER_STEPINIT_SHIPPING_AMOUNT') }}</th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="(servicePoint, index) in servicePoints"
                    :key="servicePoint.servicePointExtId"
                  >
                    <td :data-index="index" :data-cy="`service-point-${index}`">
                      <v-icon
                        v-if="isStep1ReadOnly || !canEditOffer"
                        :color="servicePointIcon(servicePoint.servicePointExtId).color"
                        small
                        right
                      >
                        {{ servicePointIcon(servicePoint.servicePointExtId).icon }}
                      </v-icon>

                      <v-radio
                        v-else
                        :value="servicePoint.servicePointExtId"
                        :key="servicePoint.servicePointExtId"
                      />
                    </td>

                    <td
                      :data-index="index"
                      :data-cy="`service-point-${index}-address`"
                    >
                      {{ servicePoint.address1 }}, {{ servicePoint.cityName }}
                    </td>

                    <td
                      :class="shipmentsClass"
                      @click.stop="handleEditNumber(servicePoint)"
                      :data-cy="`service-point-amount-${index}`"
                    >
                      {{ servicePoint.compromisedShipments }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <div class="mt-2 mb-4">
              <p
                v-if="favError"
                class="error-msg mt-2"
                data-cy="warning-favorite-point"
              >
                {{ $t('STEPPER_STEPINIT_FAVORITE_ERROR') }}
              </p>

              <p
                v-if="totalShipmentsError"
                class="error-msg mt-2"
                data-cy="warning-shipping-amount"
                v-html="totalShipmentsErrorText"
              />
            </div>
          </v-radio-group>
        </div>

        <div v-if="!isStep1ReadOnly">
          <p class="text-h2 mt-8 mb-4">
            {{ $t('STEPPER_STEPINIT_CLONE_OFFER_TITLE') }}
          </p>

          <v-text-field
            outlined
            class="select-field--reduced text-body-2"
            :placeholder="$t('STEPPER_STEPINIT_CLONE_OFFER_PLACEHOLDER')"
            v-model="cloneOfferId"
          />

          <v-btn color="primary" @click="cloneOffer">
            {{ $t('STEPPER_STEPINIT_CLONE_OFFER_BUTTON') }}
          </v-btn>
        </div>
      </v-form>
    </v-col>

    <SummaryCard/>
  </v-row>
</template>

<script>
import NumberOfShipmentsEditionDialog from '@/containers/offer-form/step-init/NumberOfShipmentsEditionDialog'
import SummaryCard                       from '@/components/summary-card'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import {
  REQUEST_MODELS,
  REQUEST_ORGANIZATION,
  CLONE_OFFER
} from '@/store/modules/offer/actions'
import {
  SET_OFFER
} from '@/store/modules/offer/mutations'
import { GET_OFFER_ITEM } from '@/store/modules/offer/getters'
import {
  SET_SERVICE_POINT_SHIPMENTS,
  SET_FAV_SERVICE_POINT,
  SET_TARIFF_MODEL,
  SET_LEGAL_CITY,
  SET_B2B_DISTRIBUTION,
  SET_B2C_DISTRIBUTION
} from '@/store/modules/offer/mutations'
import { OFFER_STATUS, DEFAULT_RATE_MODEL_ID }   from '@/helpers/constants'
import { SET_CURRENT_MODEL } from '@/store/modules/rateModel'

export default {
  name: 'StepInit',
  components: { SummaryCard, NumberOfShipmentsEditionDialog },

  data () {
    return {
      numberOfShipmentsEditionDialog: false,
      shippingModalInfo: {},
      areAddresssesVisible: false,
      rateModelsAvailable: [],
      tariffSelected: 0,
      legalCity: undefined,
      b2b: undefined,
      b2c: undefined,
      numericRules: {
        numberOnly: value => /^\d+$/.test(value) || 'Solo se permiten números',
      },
      showDistributionError: false,

      tariffRules: {
        required: value => !!value || this.$t('STEPPER_STEP4_ERROR_MESSAGE')
      },
      
      distributionRules: {
        required: value => !!value || this.$t('STEPPER_STEP4_ERROR_MESSAGE'),
        validateSum: () => {
          const sum = Number(this.b2b) + Number(this.b2c);
          return sum === 100 || this.$t('STEPPER_DISTRIBUTION_ERROR')
        }
      },

      favIcon:    { color: 'primary', icon: 'mdi-circle' },
      notFavIcon: { color: 'grey',    icon: 'mdi-circle-outline' },

      cloneOfferId: ''
    }
  },

  computed: {
    ...mapGetters('offer',  [ GET_OFFER_ITEM, 'canEditOffer' ]),
    ...mapGetters('process',['isStep1ReadOnly']),

    offerSavedTariff ()    { return this[GET_OFFER_ITEM]('rateModelId') },
    offerSavedLegalCity () { return this[GET_OFFER_ITEM]('legalCity')   },
    offerId ()             { return this[GET_OFFER_ITEM]('offerId')     },

    rateModelDesc () { return this[GET_OFFER_ITEM]('rateModelDesc') },

    servicePoints () { return this[GET_OFFER_ITEM]('optyServicePoints') },

    isOfferSignedOrSigning () {
      const status = this[GET_OFFER_ITEM]('status')
      return status === OFFER_STATUS.SIGNED || status === OFFER_STATUS.SIGNING
    },

    legalCities () {
      const modelId = this.tariffSelected || this.offerSavedTariff
      const model   = this.rateModelsAvailable.find(m => m.id === modelId)
      return model?.legalCities || []
    },

    hasServicePoints () { return Boolean(this.servicePoints.length) },
    showServicePoints () {
      return this.hasServicePoints &&
            (this.isStep1ReadOnly || this.areAddresssesVisible)
    },

    tariffSelectedLabel () {
      if (!this.isStep1ReadOnly) return ''
      return this.tariffSelected !== 0
        ? this.rateModelsAvailable?.find(el => el.id === this.tariffSelected)?.description
        : this.rateModelDesc.key
    },

    favoriteServicePoint: {
      get () {
        return this.servicePoints.find(sp => sp.isFavorite)?.servicePointExtId
      },
      set (servicePointExtId) {
        if (!this.isStep1ReadOnly) this.SET_FAV_SERVICE_POINT(servicePointExtId)
      }
    },

    clientCommitedDeliveries () { return this[GET_OFFER_ITEM]('clientCommitedDeliveries') },
    totalShipmentsOfServicePoints () {
      return this.servicePoints.reduce((acc, sp) => acc + Number(sp.compromisedShipments), 0)
    },
    shipmentsDifference () { return this.clientCommitedDeliveries - this.totalShipmentsOfServicePoints },
    totalShipmentsError  () { return this.shipmentsDifference !== 0 },
    
    shouldShowDistributionFields () {
      // Verificar si hay modelos disponibles
      const availableModelIds = this.rateModelsAvailable?.map(model => model.id) || [];
      
      // Mostrar los campos si hay un modelo tarifario seleccionado localmente y está en la lista de disponibles
      if (this.tariffSelected !== 0 && availableModelIds.includes(this.tariffSelected)) {
        return true;
      }
      
      // O si hay un modelo tarifario en la oferta y está en la lista de disponibles
      const offer = this[GET_OFFER_ITEM]();
      if (offer && offer.rateModelId && availableModelIds.includes(offer.rateModelId)) {
        return true;
      }
      
      // O si hay una descripción de modelo tarifario visible en la UI y corresponde a un modelo disponible
      if (this.rateModelDesc && this.rateModelDesc.key) {
        // Buscar si algún modelo disponible corresponde a esta descripción
        const modelExists = this.rateModelsAvailable?.some(model => 
          model.description === this.rateModelDesc.key || 
          model.id === this.rateModelDesc.key
        );
        
        if (modelExists) {
          return true;
        }
      }
      
      // O si estamos en modo de solo lectura y hay un modelo seleccionado que existe en el desplegable
      if (this.isStep1ReadOnly && this.rateModelsAvailable?.find(model => model.description === this.computedRateModelDescription)) {
        return true;
      }
      
      return false;
    },
    totalShipmentsErrorText () {
      const trnsl = this.shipmentsDifference > 0
        ? 'STEPPER_STEPINIT_SHIPPING_AMOUNT_GAP_ERROR'
        : 'STEPPER_STEPINIT_SHIPPING_AMOUNT_EXCESS_ERROR'
      return this.$t(trnsl, {
        diff:   Math.abs(this.shipmentsDifference),
        total:  this.clientCommitedDeliveries
      })
    },
    favError () { return !this.favoriteServicePoint },
    shipmentsClass () {
      if (this.isStep1ReadOnly) return null
      return this.totalShipmentsError ? 'clickable-error' : 'clickable'
    },

    hasError () {
      // Ciudad jurídica es siempre necesaria si hay ciudades disponibles (igual que el modelo tarifario)
      const needsLegalCity = this.legalCities.length && !this.legalCity

      return (
        (this.tariffSelected === 0 || this.tariffSelected === DEFAULT_RATE_MODEL_ID) ||
        needsLegalCity ||
        (this.hasServicePoints && (this.favError || this.totalShipmentsError))
      )
    },
    isContinueDisabled () {
      return this.hasError && !this.isStep1ReadOnly
    }
  },

  methods: {
    ...mapActions('offer',  [ REQUEST_MODELS, REQUEST_ORGANIZATION, CLONE_OFFER ]),
    ...mapMutations('offer', [
      SET_SERVICE_POINT_SHIPMENTS,
      SET_FAV_SERVICE_POINT,
      SET_TARIFF_MODEL,
      SET_LEGAL_CITY,
      SET_B2B_DISTRIBUTION,
      SET_B2C_DISTRIBUTION,
      SET_OFFER
    ]),
    
    numberOnly(evt) {
      const charCode = evt.which ? evt.which : evt.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        evt.preventDefault();
        return false;
      }
      return true;
    },
    ...mapActions('rateModel', [ SET_CURRENT_MODEL ]),
    
    validateDistribution(source) {
      // Convertir valores a números
      let b2b = Number(this.b2b);
      let b2c = Number(this.b2c);
      
      // Asegurar que los valores estén en el rango 0-100
      if (b2b < 0) b2b = 0;
      if (b2b > 100) b2b = 100;
      if (b2c < 0) b2c = 0;
      if (b2c > 100) b2c = 100;
      
      // Ajustar automáticamente para sumar 100
      if (source === 'b2b') {
        // Si se modificó b2b, ajustar b2c
        b2c = 100 - b2b;
      } else {
        // Si se modificó b2c o es la inicialización, ajustar b2b
        b2b = 100 - b2c;
      }
      
      // Actualizar los valores en la UI
      this.b2b = b2b;
      this.b2c = b2c;
      
      // Ya no es necesario mostrar error, siempre es válido
      this.showDistributionError = false;
      
      // Usar las mutaciones del store igual que los otros campos
      this.SET_B2B_DISTRIBUTION(b2b);
      this.SET_B2C_DISTRIBUTION(b2c);
      
      // IMPORTANTE: También actualizar la oferta directamente para asegurar que se envía al backend
      const offer = this[GET_OFFER_ITEM]();
      if (offer) {
        offer.b2b = b2b;
        offer.b2c = b2c;
        this.SET_OFFER(offer);
      }
    },

    ...mapActions('alerts',['createAlert']),
    ...mapMutations('alerts',['removeAlert']),
    ...mapActions('rateModel',[ SET_CURRENT_MODEL ]),

    async getRateModelsAvailable () {
      try {
        const rateModelId = this.offerSavedTariff
        
        // Siempre intentar obtener la organización del modelo actual para filtrar correctamente
        // Esto funciona tanto para modelos por defecto (ES/PT) como para ofertas existentes
        if (rateModelId && rateModelId !== 0) {
          const organizationResponse = await this.REQUEST_ORGANIZATION(String(rateModelId))
          const organizationId = organizationResponse?.data?.organization
          const { data } = await this.REQUEST_MODELS(organizationId)
          this.rateModelsAvailable = data
        } else {
          // Solo si no hay rateModelId (caso extraño), cargar todos
          const { data } = await this.REQUEST_MODELS(null)
          this.rateModelsAvailable = data
        }
        
        // Sincronizar tariffSelected después de cargar los modelos
        if (this.offerSavedTariff && this.offerSavedTariff !== 0) {
          this.tariffSelected = this.offerSavedTariff
        }
      } catch (err) {
        console.error('Error obteniendo modelos de tarifa:', err)
        // En caso de error, intentar cargar todos los modelos como fallback
        try {
          const { data } = await this.REQUEST_MODELS(null)
          this.rateModelsAvailable = data
        } catch (fallbackErr) {
          console.error('Error en fallback de modelos:', fallbackErr)
        }
      }
    },

    selectTariff () {
      console.log('selectTariff')
      this.legalCity = undefined
      this.selectLegalCity()                    
      this.SET_CURRENT_MODEL(this.tariffSelected)
      this.SET_TARIFF_MODEL(this.tariffSelected)
      
      // Inicializar valores B2B y B2C por defecto igual que los otros campos
      this.b2b = 50;
      this.b2c = 50;
      this.SET_B2B_DISTRIBUTION(this.b2b);
      this.SET_B2C_DISTRIBUTION(this.b2c);
      
      // IMPORTANTE: También actualizar la oferta directamente para asegurar que se envía al backend
      const offer = this[GET_OFFER_ITEM]();
      if (offer) {
        offer.b2b = this.b2b;
        offer.b2c = this.b2c;
        this.SET_OFFER(offer);
      }
      
      this.showDistributionError = false;
      this.areAddresssesVisible = true;
    },

    selectLegalCity () { this.SET_LEGAL_CITY(this.legalCity) },

    openNumberOfShipmentsEditionDialog (id, shippingAmount) {
      this.numberOfShipmentsEditionDialog = true
      this.shippingModalInfo = { id, shippingAmount }
    },
    changeShipping (newAmount, { id }) {
      this.SET_SERVICE_POINT_SHIPMENTS({
        servicePointExtId: id,
        compromisedShipments: newAmount
      })
    },

    servicePointIcon (servPointId) {
      return servPointId === this.favoriteServicePoint
        ? this.favIcon
        : this.notFavIcon
    },

    async cloneOffer () {
      this.removeAlert('cloneOffer')
      const { errorCode } = await this[CLONE_OFFER]({
        offerId:      this.offerId,
        sourceOfferId:this.cloneOfferId
      })

      if (errorCode) {
        this.createAlert({
          id:'cloneOffer',
          type:'error',
          contentKey:`STEPPER_STEPINIT_CLONE_OFFER_ERROR_${errorCode}`
        })
      } else {
        window.navigation.reload()
      }
    },
    

  },

  mounted () {
    // Métodos eliminados: no existen y causaban errores
  },
  
  created () {
    // Inicializar tariffSelected con el valor guardado en la oferta
    if (this.offerSavedTariff && this.offerSavedTariff !== 0) {
      this.tariffSelected = this.offerSavedTariff
    }
    
    // Cargar modelos disponibles (asíncrono)
    this.getRateModelsAvailable()
    
    // Inicializar valores B2B/B2C desde el store (igual que los otros campos)
    const b2bValue = this[GET_OFFER_ITEM]('b2b') || 50;
    const b2cValue = this[GET_OFFER_ITEM]('b2c') || 50;
    this.b2b = b2bValue;
    this.b2c = b2cValue;
    this.SET_B2B_DISTRIBUTION(b2bValue);
    this.SET_B2C_DISTRIBUTION(b2cValue);
    
    // Inicializar legalCity desde la oferta si existe
    this.legalCity = this[GET_OFFER_ITEM]('legalCity');
  },

  watch: {
    isContinueDisabled: {
      handler (val) {
        this.$emit('onChangeIsContinueForbidden', val)
      },
      immediate: true
    }
  }
}
</script>

<style scoped lang="scss">
.select-field {
  &--reduced { max-width: rem(420); }
}

.error-msg { color: $primary; }

.clickable, .clickable-error {
  cursor: pointer;
  transition: all .35s ease-in-out !important;
}

.clickable:hover {
  background-color: $light-grey;
  color: $primary;
}

.clickable-error {
  color: $primary;
}

.clickable-error:hover {
  background-color: $light-grey;
}
</style>
